import * as React from 'react'
import {Navigate, useRouteError, type RouteObject} from 'react-router'

import LoginPage from '@frontrow/console/src/components/pages/login-page'
import TeachersPage from '@frontrow/console/src/components/pages/teachers-page'
import {TeacherLicensesContainer} from '@frontrow/console/src/components/pages/teachers-page/teacher-licenses'
import StudentsPageContainer from '@frontrow/console/src/components/pages/students-page'
import SchoolLicensesContainer from '@frontrow/console/src/components/pages/schools-page/school-licenses'
import {EditSchoolContainer} from '@frontrow/console/src/components/pages/edit-school-page'
import SchoolsPageContainer from '@frontrow/console/src/components/pages/schools-page'
import {DistrictLicensesContainer} from '@frontrow/console/src/components/pages/districts-page/district-licenses'
import {DistrictsRgpCsvsContainer} from '@frontrow/console/src/components/pages/districts-rgp-csv-page'
import {DistrictsPageContainer} from '@frontrow/console/src/components/pages/districts-page'
import {SchoolAdminsPageContainer} from '@frontrow/console/src/components/pages/school-admins-page'
import RosterPageContainer from '@frontrow/console/src/components/pages/roster-page'
import {ImportSisIdsPageContainer} from '@frontrow/console/src/components/pages/import-sis-ids'
import RosterSyncPageContainer from '@frontrow/console/src/components/pages/roster-page/roster-sync-page'
import IdpOverlayStudentsPageContainer from '@frontrow/console/src/components/pages/roster-page/idp-overlay-students-page'
import {PiggyStoreContainer} from '@frontrow/console/src/components/pages/piggy-store-page'
import {CreateItemContainer} from '@frontrow/console/src/components/pages/piggy-store-page/create-item'
import {EditItemContainer} from '@frontrow/console/src/components/pages/piggy-store-page/edit-item'
import JobsPageContainer from '@frontrow/console/src/components/pages/jobs-page'
import {DemoDistrictContainer as DemoContainer} from '@frontrow/console/src/components/pages/demo-districts-page'
import {SalesReportsContainer} from '@frontrow/console/src/components/pages/sales-reports-page'
import {StandardSetsContainer} from '@frontrow/console/src/components/pages/standard-sets-page'
import {SpinnerOverlay} from '@frontrow/console/src/components/spinner'
import {
  shouldRecheckSession,
  signedInLoader,
  SignedInLayout
} from '@frontrow/console/src/routers/signed-in-layout'
import {BulkEditSchoolConfiguration} from '@frontrow/console/src/components/pages/bulk-edit-school-configuration'

// A data router (rather than `<BrowserRouter>`) is needed for `useBlocker` in
// `UnsavedChanges`. Paths ending in `/*` were non-exact routes under v5.
export const routes: Array<RouteObject> = [
  {
    errorElement: <RethrowRouteError />,
    children: [
      {path: '/', element: <Navigate to="/teachers" replace />},
      {path: '/login/*', element: <LoginPage />},
      {
        loader: signedInLoader,
        shouldRevalidate: shouldRecheckSession,
        hydrateFallbackElement: <SpinnerOverlay />,
        element: <SignedInLayout />,
        children: [
          {path: '/teachers', element: <TeachersPage />},
          {path: '/teachers/:teacherId/licenses', element: <TeacherLicensesContainer />},
          {path: '/students/*', element: <StudentsPageContainer />},
          {path: '/schools', element: <SchoolsPageContainer />},
          {path: '/districts', element: <DistrictsPageContainer />},
          {path: '/school-admins', element: <SchoolAdminsPageContainer />},
          {path: '/roster/*', element: <RosterPageContainer />},
          {path: '/import-sis-ids/*', element: <ImportSisIdsPageContainer />},
          {path: '/roster-sync/*', element: <RosterSyncPageContainer />},
          {path: '/idp-overlay-students/*', element: <IdpOverlayStudentsPageContainer />},
          {path: '/items', element: <PiggyStoreContainer />},
          {path: '/items/:itemId/edit/*', element: <EditItemContainer />},
          {path: '/items/create/*', element: <CreateItemContainer />},
          {path: '/schools/:schoolId/edit', element: <EditSchoolContainer />},
          {path: '/schools/:schoolId/licenses', element: <SchoolLicensesContainer />},
          {path: '/schools/edit/batch', element: <BulkEditSchoolConfiguration />},
          {path: '/districts/:districtId/licenses', element: <DistrictLicensesContainer />},
          {
            path: '/districts/:districtId/rgp-csvs/:schoolId',
            element: <DistrictsRgpCsvsContainer />
          },
          {path: '/districts/:districtId/rgp-csvs', element: <DistrictsRgpCsvsContainer />},
          {path: '/piggy-store/*', element: <PiggyStoreContainer />},
          {path: '/demo-district/*', element: <DemoContainer />},
          {path: '/sales-reports/*', element: <SalesReportsContainer />},
          {path: '/standard-sets/*', element: <StandardSetsContainer />},
          {path: '/jobs/*', element: <JobsPageContainer />}
        ]
      },
      // v5 rendered an empty page for an unknown path. Without this route the
      // router raises a 404, which `RethrowRouteError` would report to Bugsnag.
      {path: '*', element: null}
    ]
  }
]

// The data router catches render errors in its own boundary. Rethrowing hands
// them to the Bugsnag error boundary around `AppRouter` in `main.tsx`.
function RethrowRouteError(): React.ReactElement {
  throw useRouteError()
}
